import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import _ from 'lodash';

import {
    DefaultButton,
    PrimaryButton,
    Separator,
    Stack
} from '@fluentui/react';

import { RootState } from '../../../../../redux/application/redux-store';
import { RouterSettings } from '../../../../../utils/router';
import {
    Ticket,
    TicketModeEnum,
    TicketPriorityEnum,
    TicketStatusEnum,
    TicketChangeLog,
    TicketAllOfProperties,
    PropertyAttributeTypeEnum
} from '../../../../../api-client';
import { TRANSLATION_KEYS } from './localization/translation-keys';
import { useDepartmentMembers } from './hooks/useDepartmentMembers';

import {
    FormProvider,
    IAIDropdown,
    IAITextField,
    IAIEditor,
    IAIDatePicker
} from '../../../../../common/components/IAIForm';
import ChangeLogsAccordion from './components/ChangeLogsAccordion/change-logs-accordion';
import Attachment from './components/Attachment/attachment';
import { useProperties } from '../../../../Setting/SettingPropertyPage/hooks/useProperties';

interface FormValuesProps extends Ticket {
    areaKey: string;
    departmentKey: string;
    agentKey: string;
    customAttributesVertical: {
        id: string;
        areaId?: string;
        departmentId?: string;
        label: string;
        value?: any;
        options?: string[];
        type: PropertyAttributeTypeEnum;
    }[];
}

interface IDetailsForm {
    ticket?: Ticket;
    disabled?: boolean;
    isEdit?: boolean;
    changeLogs?: TicketChangeLog[];
    onFormConfirm: (dataItem: Ticket) => void;
}

const DetailsForm: React.FunctionComponent<IDetailsForm> = ({
    ticket,
    disabled,
    isEdit = false,
    changeLogs,
    onFormConfirm
}) => {
    const {
        i18n,
        t: translate
    } = useTranslation();

    const {
        handleProperties,
        properties: customAttributes
    } = useProperties();

    //#region Form-Configuration

    const formDataSchema = Yup.object().shape({
        mode: Yup.string().required(translate(TRANSLATION_KEYS.ModeErrorMessage)),
        areaKey: Yup.string().required(translate(TRANSLATION_KEYS.AreaErrorMessage)),
        departmentKey: Yup.string().required(translate(TRANSLATION_KEYS.DepartmentErrorMessage)),
        agentKey: Yup.string(),
        status: Yup.string().required(translate(TRANSLATION_KEYS.StatusErrorMessage)),
        priority: Yup.string(),
        subject: Yup.string().required(translate(TRANSLATION_KEYS.SubjectErrorMessage)),
        description: Yup.string().required(translate(TRANSLATION_KEYS.DescriptionErrorMessage)),
        note: Yup.string(),
        customAttributesVertical: Yup.array()
    });

    const defaultValues = useMemo(
        () => ({
            mode: ticket?.mode || TicketModeEnum.Level01,
            areaKey: ticket?.area?.id || '',
            departmentKey: ticket?.department?.id || '',
            agentKey: ticket?.agent?.id || '',
            status: ticket?.status || TicketStatusEnum.Open,
            priority: ticket?.priority || TicketPriorityEnum.Medium,
            subject: ticket?.subject || '',
            description: ticket?.description || '',
            note: ticket?.note || '',
            customAttributesVertical: _.map(customAttributes, dataItem => {
                const existingProps = _.find(ticket?.properties, x => x.id === dataItem.id);

                return ({
                    id: dataItem.id,
                    areaId: dataItem.areaId,
                    departmentId: dataItem.departmentId,
                    label: dataItem.label,
                    value: dataItem.attributeType === PropertyAttributeTypeEnum.Date && !!existingProps?.value
                        ? new Date(existingProps.value)
                        : existingProps?.value || undefined,
                    options: dataItem.options || [],
                    type: dataItem.attributeType || PropertyAttributeTypeEnum.Combo
                });
            })
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ticket, customAttributes]
    );

    const methods = useForm<FormValuesProps>({ resolver: yupResolver(formDataSchema), defaultValues });

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { isSubmitting }
    } = methods;

    const {
        fields
    } = useFieldArray({
        control,
        name: 'customAttributesVertical'
    });

    useEffect(() => {
        handleProperties();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticket, customAttributes]);

    const formValues = watch();

    //#endregion

    //#region Dropdown-Management

    const iaiSystem = useSelector((state: RootState) => state.system);

    const areaOptions = useMemo(
        () => _.map(iaiSystem.areas, entry => ({ key: entry.id!, text: entry.displayName! })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [iaiSystem.areas]
    );

    const departmentOptions = useMemo(
        () => {
            const selectedArea = _.find(iaiSystem.areas, x => x.id === formValues.areaKey);

            return !!selectedArea
                ? _.map(selectedArea.departments, entry => ({ key: entry.id!, text: entry.displayName! }))
                : [];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formValues.areaKey]
    );

    const {
        handleRetrieveMembers,
        retrieveMembers,
        retrieveMembersLoading
    } = useDepartmentMembers();

    useEffect(() => {
        if (isEdit && !!formValues.departmentKey)
            handleRetrieveMembers({ id: formValues.departmentKey });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.departmentKey]);

    const agentOptions = useMemo(
        () => formValues.departmentKey
            ? _.map(retrieveMembers, entry => ({ key: entry.key!, text: entry.text! }))
            : [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [retrieveMembers, formValues.departmentKey]
    );

    //#endregion

    const iaiAccount = useSelector((state: RootState) => state.account);

    const _changeFormUnauthorize = () => (
        (!!ticket) &&
        (ticket.status === 'canceled' || ticket.status === 'closed' || ticket.status === 'deleted') &&
        (iaiAccount.role === 'departmentAgent' || iaiAccount.role === 'user')
    );

    const navigate = useNavigate();

    const _handleDismiss = () => {
        navigate(RouterSettings.PATHS.dashboard.ticket.root);
    }

    const _handleSubmit = (data: FormValuesProps) => {
        const areaPayload = _.find(iaiSystem.areas, entry => entry.id === data.areaKey);
        const departmentPayload = _.find(areaPayload?.departments, entry => entry.id === data.departmentKey);
        const agentPayload = _.find(retrieveMembers, entry => entry.id === data.agentKey);

        const ticketPayload = {
            id: ticket?.id,
            mode: TicketModeEnum.Level01,
            area: !!areaPayload
                ?
                {
                    tenantId: areaPayload.tenantId,
                    id: areaPayload.id,
                    displayName: areaPayload.displayName
                }
                : undefined,
            department: !!departmentPayload
                ?
                {
                    tenantId: departmentPayload.tenantId,
                    id: departmentPayload.id,
                    displayName: departmentPayload.displayName,
                    groups: departmentPayload.groups
                }
                : undefined,
            agent: !!agentPayload
                ?
                {
                    tenantId: agentPayload.tenantId,
                    id: agentPayload.id,
                    displayName: agentPayload.displayName,
                    mailAddress: agentPayload.mailAddress
                }
                : undefined,
            status: data.status,
            priority: data.priority,
            properties: _.map(_.filter(data.customAttributesVertical, x => !!x.value), x => ({
                id: x.id,
                value: x.type === PropertyAttributeTypeEnum.Date && !!x.value
                    ? moment(x.value)
                        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
                        .format('YYYY-MM-DD')
                    : x.value,
            })),
            subject: data.subject,
            description: data.description,
            note: data.note,
            // Legacy Properties
            readableId: ticket?.readableId,
            requestedBy: ticket?.requestedBy,
            createdAt: ticket?.createdAt,
            modifiedAt: ticket?.modifiedAt,
            canceledAt: ticket?.canceledAt,
            closedAt: ticket?.closedAt,
            deletedAt: ticket?.deletedAt,
        };

        onFormConfirm(ticketPayload);
    }

    const disableCondition = disabled || _changeFormUnauthorize() || isSubmitting;

    return (
        <FormProvider methods={methods}>
            <Stack tokens={{ childrenGap: 15 }}>
                <div className="row">
                    <div className="col-12 col-md-4">
                        <IAIDropdown
                            name="areaKey"
                            label={translate(TRANSLATION_KEYS.AreaLabel)}
                            placeholder={translate(TRANSLATION_KEYS.AreaPlaceholder)}
                            required
                            disabled={disableCondition || retrieveMembersLoading || !areaOptions.length}
                            options={areaOptions}
                            onChange={(ev, option) => {
                                if (option) {
                                    setValue('areaKey', option.key as string);
                                    setValue('departmentKey', '');
                                    setValue('agentKey', '');

                                    const selectedAreaDepartments = _.find(iaiSystem.areas, entry => entry.key === option.key)?.departments || [];

                                    selectedAreaDepartments.length === 1 &&
                                        setValue('departmentKey', selectedAreaDepartments[0].key!);
                                }
                            }}
                        />
                    </div>
                    <div className="col-12 col-md-4">
                        <IAIDropdown
                            name="departmentKey"
                            label={translate(TRANSLATION_KEYS.DepartmentLabel)}
                            placeholder={translate(TRANSLATION_KEYS.DepartmentPlaceholder)}
                            required
                            disabled={disableCondition || retrieveMembersLoading || !departmentOptions.length}
                            options={departmentOptions}
                            onChange={(ev, option) => {
                                if (option) {
                                    setValue('departmentKey', option.key as string);
                                    setValue('agentKey', '');
                                }
                            }}
                        />
                    </div>

                    {isEdit && (
                        <div className="col-12 col-md-4">
                            <IAIDropdown
                                name="agentKey"
                                label={translate(TRANSLATION_KEYS.AgentLabel)}
                                placeholder={translate(TRANSLATION_KEYS.AgentPlaceholder)}
                                disabled={disableCondition || retrieveMembersLoading || !agentOptions.length}
                                options={agentOptions}
                            />
                        </div>
                    )}
                </div>
                <div className="row">
                    {isEdit && (
                        <div className="col-12 col-md-4">
                            <IAIDropdown
                                name="status"
                                label={translate(TRANSLATION_KEYS.StatusLabel)}
                                placeholder={translate(TRANSLATION_KEYS.StatusPlaceholder)}
                                required
                                disabled={disableCondition}
                                options={[
                                    { key: TicketStatusEnum.Canceled, text: translate(TRANSLATION_KEYS.StatusOptionCanceled) },
                                    { key: TicketStatusEnum.Closed, text: translate(TRANSLATION_KEYS.StatusOptionClosed) },
                                    // { key: TicketStatusEnum.Deleted, text: translate(TRANSLATION_KEYS.StatusOptionDeleted) },
                                    { key: TicketStatusEnum.Open, text: translate(TRANSLATION_KEYS.StatusOptionOpen) },
                                    { key: TicketStatusEnum.Pending, text: translate(TRANSLATION_KEYS.StatusOptionPending) },
                                    { key: TicketStatusEnum.Working, text: translate(TRANSLATION_KEYS.StatusOptionWorking) },
                                ]}
                            />
                        </div>
                    )}

                    <div className="col-12 col-md-4">
                        <IAIDropdown
                            name="priority"
                            label={translate(TRANSLATION_KEYS.PriorityLabel)}
                            placeholder={translate(TRANSLATION_KEYS.PriorityPlaceholder)}
                            disabled={disableCondition}
                            options={[
                                { key: TicketPriorityEnum.Low, text: translate(TRANSLATION_KEYS.PriorityOptionLow) },
                                { key: TicketPriorityEnum.Medium, text: translate(TRANSLATION_KEYS.PriorityOptionMedium) },
                                { key: TicketPriorityEnum.High, text: translate(TRANSLATION_KEYS.PriorityOptionHigh) }
                            ]}
                        />
                    </div>
                </div>

                <Separator />

                {!!fields.length && (
                    <div className="row">
                        {_.map(fields, (dataItem, dataIndex) => {
                            const isVisible = (!dataItem.areaId && !dataItem.departmentId) ||
                                (dataItem.areaId === formValues.areaKey && dataItem.departmentId === formValues.departmentKey);

                            if (isVisible) {
                                return (
                                    <div key={dataItem.id} className="col-12 col-md-4">
                                        {dataItem.type === PropertyAttributeTypeEnum.Combo && (
                                            <IAIDropdown
                                                name={`customAttributesVertical[${dataIndex}].value`}
                                                label={dataItem.label}
                                                placeholder={translate(TRANSLATION_KEYS.ComboPlaceholder)}
                                                disabled={disableCondition}
                                                options={_.map(dataItem.options, x => ({ key: x, text: x }))}
                                            />
                                        )}
                                        {dataItem.type === PropertyAttributeTypeEnum.Date && (
                                            <IAIDatePicker
                                                name={`customAttributesVertical[${dataIndex}].value`}
                                                label={dataItem.label}
                                                placeholder={translate(TRANSLATION_KEYS.DatePlaceholder)}
                                                disabled={disableCondition}
                                            />
                                        )}
                                        {dataItem.type === PropertyAttributeTypeEnum.Input && (
                                            <IAITextField
                                                name={`customAttributesVertical[${dataIndex}].value`}
                                                label={dataItem.label}
                                                placeholder={translate(TRANSLATION_KEYS.InputPlaceholder)}
                                                disabled={disableCondition}
                                            />
                                        )}
                                    </div>
                                );
                            }
                        })}
                    </div>
                )}

                <Separator />

                <IAITextField
                    name="subject"
                    label={translate(TRANSLATION_KEYS.SubjectLabel)}
                    placeholder={translate(TRANSLATION_KEYS.SubjectPlaceholder)}
                    required
                    disabled={disableCondition}
                />

                <IAIEditor
                    name="description"
                    label={translate(TRANSLATION_KEYS.DescriptionLabel)}
                    placeholder={translate(TRANSLATION_KEYS.DescriptionPlaceholder)}
                    required
                    readOnly={disableCondition}
                />

                {isEdit && (
                    <Stack tokens={{ childrenGap: 25 }}>
                        <IAITextField
                            name="note"
                            label={translate(TRANSLATION_KEYS.NoteLabel)}
                            placeholder={translate(TRANSLATION_KEYS.NotePlaceholder)}
                            multiline
                            rows={5}
                            resizable={false}
                            disabled={disableCondition}
                        />

                        {iaiAccount.role === 'administrator' && (
                            <ChangeLogsAccordion changeLogs={changeLogs} />
                        )}
                    </Stack>
                )}

                <Separator />

                <Attachment ticketId={ticket?.id} disabled={disableCondition} />

                <Separator />

                <Stack horizontal horizontalAlign={'end'} tokens={{ childrenGap: 15 }}>
                    <DefaultButton
                        disabled={disableCondition}
                        iconProps={{ iconName: 'Cancel' }}
                        styles={{ root: { marginRight: 10 } }}
                        onClick={_handleDismiss}
                    >
                        {translate(TRANSLATION_KEYS.DismissText)}
                    </DefaultButton>
                    <PrimaryButton
                        type="submit"
                        disabled={disableCondition}
                        iconProps={{ iconName: 'CheckMark' }}
                        onClick={handleSubmit(_handleSubmit)}
                    >
                        {translate(TRANSLATION_KEYS.ConfirmText)}
                    </PrimaryButton>
                </Stack>
            </Stack>
        </FormProvider>
    );
}

export default DetailsForm;